import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { HorseWithCost, OwnerCost, DewormingParams } from './types';

// Set up fonts for pdfMake
(pdfMake as any).vfs = pdfFonts;

/**
 * PDF Styles
 */
const myStyles = () => {
  return {
    company: {
      color: 'black',
      fontSize: 12
    },
    companySmall: {
      color: 'grey',
      fontSize: 8,
      alignment: 'center'
    },
    header: {
      bold: true,
      color: '#ee0000',
      fontSize: 24
    },
    medikament: {
      bold: true,
      color: '#ee0000',
      fontSize: 16
    },
    infoText: {
      fontSize: 12,
      color: '#856404',
      italics: true,
      margin: [0, 10, 0, 10]
    },
    nameColumn: {
      alignment: 'left',
      margin: [5, 5, 10, 10]
    },
    euroColumn: {
      bold: true,
      alignment: 'right',
      margin: [5, 5, 10, 10]
    },
    headerCells: {
      bold: true,
      color: 'white'
    }
  };
};

/**
 * Create table body for cost per owner (for billing)
 */
const tableBody = (costPerOwner: OwnerCost[]) => {
  const result: any[] = [];
  const sortedList = costPerOwner.sort((a, b) => a.name.localeCompare(b.name));

  result.push([
    {
      text: 'Besitzer',
      style: 'headerCells'
    },
    {
      text: 'Summe €',
      style: 'headerCells'
    },
    {
      text: 'bezahlt',
      style: 'headerCells'
    },
    {
      text: 'quittiert',
      style: 'headerCells'
    }
  ]);

  sortedList.forEach((owner) => {
    result.push([
      {
        text: owner.name,
        style: 'nameColumn'
      },
      {
        text: owner.preis,
        style: 'euroColumn'
      },
      '',
      ''
    ]);
  });

  return result;
};

/**
 * Create table body with details per animal (for billing)
 */
const tableBodyDetails = (costPerAnimal: HorseWithCost[]) => {
  const result: any[] = [];
  const sortedList = costPerAnimal.sort((a, b) => a.name.localeCompare(b.name));

  result.push([
    {
      text: 'Pferd',
      style: 'headerCells'
    },
    {
      text: 'Besitzer',
      style: 'headerCells'
    },
    {
      text: 'Gewicht',
      style: 'headerCells'
    },
    {
      text: 'Preis',
      style: 'headerCells'
    }
  ]);

  sortedList.forEach((horse) => {
    result.push([
      {
        text: horse.name,
        style: 'nameColumn'
      },
      {
        text: horse.owner,
        style: 'nameColumn'
      },
      {
        text: horse.weight,
        style: 'nameColumn'
      },
      {
        text: horse.wkPreis,
        style: 'euroColumn'
      }
    ]);
  });

  return result;
};

/**
 * Create table body for measurement protocol (empty weight column)
 */
const tableBodyDetailsMeasure = (costPerAnimal: HorseWithCost[]) => {
  const result: any[] = [];
  const sortedList = costPerAnimal.sort((a, b) => a.name.localeCompare(b.name));

  result.push([
    {
      text: 'Pferd',
      style: 'headerCells'
    },
    {
      text: 'Besitzer',
      style: 'headerCells'
    },
    {
      text: 'Gewicht',
      style: 'headerCells'
    }
  ]);

  sortedList.forEach((horse) => {
    result.push([
      {
        text: horse.name,
        style: 'nameColumn'
      },
      {
        text: horse.owner,
        style: 'nameColumn'
      },
      {
        text: '',
        style: 'nameColumn'
      }
    ]);
  });

  return result;
};

/**
 * Format date for display
 */
const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * Create text for PDF header
 */
const textToPrint = (params: DewormingParams): string => {
  const plan = params.planning ? ' geplant' : '';
  return ` Medikament: ${params.wurmkur}, gegeben am: ${formatDate(params.tagDerGabe)}${plan}\n\n`;
};

/**
 * Generate billing PDF (Abrechnung)
 */
export const generateBillingPDF = (
  costPerOwner: OwnerCost[],
  costPerAnimal: HorseWithCost[],
  params: DewormingParams
) => {
  const docDefinition: any = {
    header: (currentPage: number, pageCount: number) => {
      return {
        margin: [40, 30, 30, 30],
        columns: [
          {
            width: '*',
            text: 'Reitsport Walther & Finger GbR\nBarriser Weg 64\n24787 Fockbek',
            style: 'company'
          },
          {
            width: '150',
            text: formatDate(new Date()) +
              '\nTel.: 01523/3697043\nmail: waltherj@web.de\nSeite ' +
              currentPage.toString() + ' von ' + pageCount,
            style: 'company',
            alignment: 'right'
          }
        ]
      };
    },
    footer: (currentPage: number, pageCount: number) => {
      return {
        margin: [40, 30, 30, 30],
        columns: [
          {
            width: '*',
            text: 'Reitsport Walther & Finger GbR\nGeschäftsführer\nDr. Lutz Finger und Joachim Walther',
            style: 'companySmall'
          },
          {
            width: '*',
            text: 'Raiffeisenbank im Kreis Rendsburg\nIBAN DE72 2169 0020 0005 4678 02\nBIC GENO DEF 1NTO',
            style: 'companySmall'
          },
          {
            width: '*',
            text: 'Finanzamt Rendsburg\nStnr. 028/226/06957',
            style: 'companySmall'
          }
        ]
      };
    },
    content: [
      {
        text: ' Abrechnung der letzten Wurmkur \n',
        style: 'header'
      },
      {
        text: textToPrint(params),
        style: 'medikament'
      },
      {
        text: 'Wichtiger Hinweis zur Gewichtsmessung: Die Wurmkur kann nur in Schritten zu 50 kg Pferdegewicht gegeben werden. Die Messung erfolgt daher in ausreichender Genauigkeit mit dem Equimax Maßband. Das abgelesene Gewicht wird auf die nächsten 50 kg aufgerundet. Die Gewichtsangaben in der Abrechnung können daher bis zu maximal 50 kg vom tatsächlichen Gewicht abweichen!\n\n',
        style: 'infoText'
      },
      {
        text: 'Pferde, die KEINE Wurmkur erhalten, sind mit einem Gewicht von 0 eingetragen!\n\n',
        style: 'medikament'
      },
      {
        margin: [0, 0, 0, 20],
        table: {
          headerRows: 1,
          widths: [200, 100, '*', '*'],
          body: tableBody(costPerOwner)
        },
        layout: {
          hLineWidth: (i: number, node: any) => {
            return (i === 1) ? 3 : 1;
          },
          hLineColor: (i: number, node: any) => {
            return (i === 1) ? 'black' : 'gray';
          },
          vLineWidth: (i: number, node: any) => {
            return 1;
          },
          vLineColor: 'grey',
          fillColor: (i: number, node: any) => {
            return (i === 0) ? '#418441' : null;
          }
        },
        pageBreak: 'after'
      },
      {
        margin: [0, 0, 0, 20],
        table: {
          headerRows: 1,
          widths: [150, 150, '*', '*'],
          body: tableBodyDetails(costPerAnimal)
        },
        layout: {
          hLineWidth: (i: number, node: any) => {
            return (i === 1) ? 3 : 1;
          },
          hLineColor: (i: number, node: any) => {
            return (i === 1) ? 'black' : 'gray';
          },
          vLineWidth: (i: number, node: any) => {
            return 1;
          },
          vLineColor: 'grey',
          fillColor: (i: number, node: any) => {
            return (i === 0) ? '#418441' : null;
          }
        }
      }
    ],
    pageSize: 'A4',
    pageMargins: [40, 90, 30, 90],
    styles: myStyles()
  };

  pdfMake.createPdf(docDefinition).open();
};

/**
 * Generate measurement protocol PDF (Messprotokoll)
 */
export const generateMeasurementPDF = (costPerAnimal: HorseWithCost[]) => {
  const docDefinition: any = {
    header: (currentPage: number, pageCount: number) => {
      return {
        margin: [40, 30, 30, 30],
        columns: [
          {
            width: '*',
            text: 'Reitsport Walther & Finger GbR\nBarriser Weg 64\n24787 Fockbek',
            style: 'company'
          },
          {
            width: '150',
            text: formatDate(new Date()) +
              '\nTel.: 01523/3697043\nmail: waltherj@web.de\nSeite ' +
              currentPage.toString() + ' von ' + pageCount,
            style: 'company',
            alignment: 'right'
          }
        ]
      };
    },
    footer: (currentPage: number, pageCount: number) => {
      return {
        margin: [40, 30, 30, 30],
        columns: [
          {
            width: '*',
            text: 'Reitsport Walther & Finger GbR\nGeschäftsführer\nDr. Lutz Finger und Joachim Walther',
            style: 'companySmall'
          },
          {
            width: '*',
            text: 'Raiffeisenbank im Kreis Rendsburg\nIBAN DE72 2169 0020 0005 4678 02\nBIC GENO DEF 1NTO',
            style: 'companySmall'
          },
          {
            width: '*',
            text: 'Finanzamt Rendsburg\nStnr. 028/226/06957',
            style: 'companySmall'
          }
        ]
      };
    },
    content: [
      {
        text: ' Messprotokoll zur nächsten Wurmkur \n',
        style: 'header'
      },
      {
        text: 'Wichtiger Hinweis zur Gewichtsmessung: Die Wurmkur kann nur in Schritten zu 50 kg Pferdegewicht gegeben werden. Die Messung erfolgt daher in ausreichender Genauigkeit mit dem Equimax Maßband. Das abgelesene Gewicht wird auf die nächsten 50 kg aufgerundet. Die Gewichtsangaben in der Abrechnung können daher bis zu maximal 50 kg vom tatsächlichen Gewicht abweichen!\n\n',
        style: 'infoText'
      },
      {
        text: 'Pferde, die KEINE Wurmkur erhalten, sind mit einem Gewicht von 0 einzutragen!\n\n',
        style: 'medikament'
      },
      {
        margin: [0, 0, 0, 20],
        table: {
          headerRows: 1,
          widths: [150, 150, '*'],
          body: tableBodyDetailsMeasure(costPerAnimal)
        },
        layout: {
          hLineWidth: (i: number, node: any) => {
            return (i === 1) ? 3 : 1;
          },
          hLineColor: (i: number, node: any) => {
            return (i === 1) ? 'black' : 'gray';
          },
          vLineWidth: (i: number, node: any) => {
            return 1;
          },
          vLineColor: 'grey',
          fillColor: (i: number, node: any) => {
            return (i === 0) ? '#418441' : null;
          }
        }
      }
    ],
    pageSize: 'A4',
    pageMargins: [40, 90, 30, 90],
    styles: myStyles()
  };

  pdfMake.createPdf(docDefinition).open();
};
